import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import api, { siteAPI, materialItemsAPI } from '../services/api';
import { createTransfer } from '../services/transfersAPI';

const TransferRequestModal = ({ open, onClose, onSuccess }) => {
  const [sites, setSites] = useState([]);
  const [materialItems, setMaterialItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fromSiteId: '',
    toSiteId: '',
    materialItemId: '',
    quantity: '',
    reason: '',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      console.log('=== FETCHING DATA FOR TRANSFER ===');
      const [sitesRes, categoriesRes, itemsRes] = await Promise.all([
        siteAPI.getAll(),
        api.get('/materials/categories'),
        materialItemsAPI.getAll()
      ]);

      console.log('Sites response:', sitesRes.data);
      console.log('Categories response:', categoriesRes.data);
      console.log('Items response:', itemsRes.data);

      const sitesArray = sitesRes.data.data?.sites || [];

      // Safely get categories array
      const categoriesData = Array.isArray(categoriesRes.data.data) 
        ? categoriesRes.data.data 
        : (categoriesRes.data.data?.categories || []);
      
      // Safely get items array
      const itemsData = Array.isArray(itemsRes.data.data) 
        ? itemsRes.data.data 
        : (itemsRes.data.data?.items || []);

      console.log('Categories data:', categoriesData);
      console.log('Items data:', itemsData);

      // Get categories (excluding Labour)
      const categories = categoriesData
        .filter(cat => cat && cat.categoryName && !cat.categoryName.toLowerCase().includes('labour'))
        .map(cat => ({
          id: `category-${cat.id}`,
          name: cat.categoryName,
          unit: cat.unit,
          type: 'category',
          categoryId: cat.id
        }));

      // Get material items (excluding Labour-related items)
      const items = itemsData
        .filter(item => item && item.itemName && !item.itemName.toLowerCase().includes('labour'))
        .filter(item => !item.itemName.toLowerCase().includes('per day'))
        .map(item => ({
          id: item.id,
          name: item.itemName,
          unit: item.unit,
          type: 'item',
          itemId: item.id
        }));

      // Combine categories and items
      const allMaterials = [...categories, ...items];

      console.log('Number of sites loaded:', sitesArray.length);
      console.log('Number of categories loaded:', categories.length);
      console.log('Number of items loaded:', items.length);
      console.log('Total materials (categories + items):', allMaterials.length);

      setSites(sitesArray);
      setMaterialItems(allMaterials);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
      console.error('Error details:', err.response?.data || err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear toSiteId if fromSiteId changes
    if (name === 'fromSiteId') {
      setFormData(prev => ({ ...prev, toSiteId: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.fromSiteId === formData.toSiteId) {
      setError('Source and destination sites must be different');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await createTransfer(formData);
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to create transfer';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      fromSiteId: '',
      toSiteId: '',
      materialItemId: '',
      quantity: '',
      reason: '',
      notes: ''
    });
    setError('');
    onClose();
  };

  const selectedMaterial = materialItems.find(m => m.id === formData.materialItemId);

  // Calculate available TO sites
  const availableToSites = sites.filter(site => site.id !== formData.fromSiteId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Request Material Transfer</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* From Site */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                label="From Site"
                name="fromSiteId"
                value={formData.fromSiteId}
                onChange={handleChange}
              >
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.siteName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* To Site */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                label="To Site"
                name="toSiteId"
                value={formData.toSiteId}
                onChange={handleChange}
                disabled={!formData.fromSiteId}
                helperText={
                  !formData.fromSiteId
                    ? "Select 'From Site' first"
                    : availableToSites.length === 0
                      ? "No other sites available"
                      : `${availableToSites.length} sites available`
                }
              >
                {availableToSites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.siteName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Material Item */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                required
                label="Material"
                name="materialItemId"
                value={formData.materialItemId}
                onChange={handleChange}
              >
                {materialItems.map((material) => (
                  <MenuItem key={material.id} value={material.id}>
                    {material.name} {material.type === 'category' ? '(Category)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
            </Grid>

            {/* Unit (Read-only) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit"
                value={selectedMaterial?.unit || ''}
                disabled
              />
            </Grid>

            {/* Reason */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={2}
                label="Reason for Transfer"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Explain why this transfer is needed..."
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Notes (Optional)"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional information..."
              />
            </Grid>

            {/* Info Box */}
            {formData.fromSiteId && formData.toSiteId && formData.materialItemId && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.dark">
                    <strong>Transfer Summary:</strong><br />
                    Requesting {formData.quantity} {selectedMaterial?.unit} of {selectedMaterial?.name}
                    <br />
                    From: {sites.find(s => s.id === formData.fromSiteId)?.siteName}
                    <br />
                    To: {sites.find(s => s.id === formData.toSiteId)?.siteName}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Requesting...' : 'Request Transfer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransferRequestModal;